import { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Collapse,
  IconButton,
  Chip,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AssessmentIcon from '@mui/icons-material/Assessment';
import VerifiedIcon from '@mui/icons-material/Verified';
import StarIcon from '@mui/icons-material/Star';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { getChartColors, formatCurrency } from '../utils/chartConfig';
import MetricExplanationModal from './MetricExplanationModal';

/**
 * CFOCapitalAnalysis - Decision-Grade CFO Capital Allocation Analysis
 *
 * Enhanced with CFO audit findings:
 * - True Cash Breakeven (not marketing payback)
 * - Break-even failure threshold
 * - Decision-grade IRR range (energy-only to 6-component)
 * - M&V approval conditions
 *
 * Philosophy: Present corpus-validated evidence from 116 PE-certified studies.
 * CFOs will apply their own discount - we don't pre-discount the data.
 */
export default function CFOCapitalAnalysis({
  roiResults,
  cfoMetrics,
  companyConfig,
  paymentMode,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const colors = getChartColors(theme.palette.mode);

  const [expanded, setExpanded] = useState(true);
  const [expandedSections, setExpandedSections] = useState({
    financial: true,
    opportunity: true,
    risk: true,
    recommendation: true,
    portfolio: true,  // Expanded by default - fleet-level context is primary framing
  });
  const [corpusData, setCorpusData] = useState(null);
  const [explanationModal, setExplanationModal] = useState(null);

  // Load corpus data for equipment comparison
  useEffect(() => {
    const loadCorpusData = async () => {
      try {
        const response = await fetch('/corpus/equipment_performance.json');
        if (response.ok) {
          const data = await response.json();
          setCorpusData(data);
        }
      } catch (e) {
        console.warn('Corpus data not available for comparison');
      }
    };
    loadCorpusData();
  }, []);

  // Calculate all CFO capital allocation metrics
  const analysis = useMemo(() => {
    if (!roiResults?.portfolio?.summary) return null;

    const {
      total_treatment_cost: capex,
      total_annual_value: annualValue,
      total_annual_savings: energyOnlySavings,
      weighted_average_payback_months: paybackMonths,
      five_year_roi: fiveYearRoi,
      total_tonnage: totalTonnage,
      total_equipment: totalEquipment,
      avg_electricity_rate: easElectricityRate,
      avg_operating_hours: easOperatingHours,
    } = roiResults.portfolio.summary;

    // value_breakdown is at portfolio level, not in summary
    const valueBreakdown = roiResults.portfolio.value_breakdown;

    const discountRate = (companyConfig?.discountRate || 8) / 100;
    // Prefer EAS file rate over CompanySetup default
    const electricityRate = easElectricityRate ?? companyConfig?.blendedElectricityRate ?? 0.10;
    const treatmentCostPerTon = totalTonnage > 0 ? capex / totalTonnage : 125;

    // ═══════════════════════════════════════════════════════════════
    // TRUE CASH BREAKEVEN - Based on energy savings only
    // ═══════════════════════════════════════════════════════════════
    const trueCashBreakevenMonths = capex / (energyOnlySavings / 12);

    // Marketing payback (uses total annual value)
    const marketingPayback = paybackMonths;
    const energyOnlyPayback = capex / (energyOnlySavings || 1) * 12;

    // ═══════════════════════════════════════════════════════════════
    // BREAK-EVEN FAILURE THRESHOLD
    // ═══════════════════════════════════════════════════════════════
    // What % of documented savings is needed to break even (NPV=0 at 5yr)?
    const minAnnualForBreakeven = capex / 3.79; // 5-yr annuity factor at 10%
    const breakEvenThresholdPct = Math.round((minAnnualForBreakeven / annualValue) * 100);

    // ═══════════════════════════════════════════════════════════════
    // NPV CALCULATIONS
    // ═══════════════════════════════════════════════════════════════
    const calculateNPV = (annualCashFlow, years = 5) => {
      let npv = -capex;
      for (let i = 1; i <= years; i++) {
        npv += annualCashFlow / Math.pow(1 + discountRate, i);
      }
      return npv;
    };

    const npv6Component = calculateNPV(annualValue);
    const npvEnergyOnly = calculateNPV(energyOnlySavings);

    // ═══════════════════════════════════════════════════════════════
    // DECISION-GRADE IRR RANGE (energy-only to 6-component)
    // ═══════════════════════════════════════════════════════════════
    const estimateIRR = (annual, cost) => {
      const payback = cost / (annual || 1) * 12;
      if (payback <= 6) return 150;
      if (payback <= 12) return Math.round(100 / (payback / 12));
      if (payback <= 24) return Math.round(50 / (payback / 24));
      return Math.round(1200 / payback);
    };

    // IRR range: low = energy-only savings, mid/high = full 6-component value
    const irrRange = {
      low: estimateIRR(energyOnlySavings, capex),
      mid: estimateIRR(annualValue, capex),
      high: Math.min(estimateIRR(annualValue, capex) * 1.2, 200),
    };

    const irrRangeDisplay = irrRange.low === irrRange.mid
      ? `${irrRange.low}%`
      : `${irrRange.low}-${Math.min(irrRange.mid, 150)}%`;

    // Value multiplier
    const valueMultiplier = annualValue / (energyOnlySavings || 1);

    // 5-year total value
    const fiveYearValue = annualValue * 5;
    const roiPerDollar = fiveYearValue / capex;

    // Capital-at-risk window
    const capitalAtRiskMonths = paymentMode === 'threeYear' ? 0 : trueCashBreakevenMonths;

    // Strategic fit scoring (4 displayed categories, all 5/5)
    const strategicScore = {
      marginImprovement: 5,
      operationalResilience: 5,
      scalability: 5,
      executionSimplicity: 5,
      average: 5.0,
    };

    // Check if mission-critical is material (>$500/year)
    const missionCriticalValue = valueBreakdown?.mission_critical_protection || 0;
    const showMissionCritical = missionCriticalValue > 500;

    // ═══════════════════════════════════════════════════════════════
    // INVESTMENT TIER CLASSIFICATION - IRR-to-WACC ratio + ECM comparison
    // ═══════════════════════════════════════════════════════════════
    // Primary: Check if IRR dramatically exceeds WACC (exceptional returns)
    // Fallback: Compare against industry ECM paybacks:
    // - VFD Installation: 24-36 months
    // - LED Retrofit: 30-48 months
    // - Equipment Replacement: 60-84 months
    // - Demand Response: 18-24 months
    let investmentTier;
    // Calculate IRR-to-WACC multiple for exceptional return detection
    const wacc = discountRate * 100; // Convert decimal to percentage
    const irrToWaccMultiple = irrRange.low / wacc;

    // First check IRR-to-WACC ratio - exceptional returns override payback logic
    if (irrToWaccMultiple >= 10) {
      // IRR is 10×+ WACC → Exceptional regardless of payback
      investmentTier = {
        label: 'Exceptional',
        color: isDark ? colors.success : 'text.primary',
        description: `IRR exceeds WACC by ${Math.round(irrToWaccMultiple)}× — exceptional risk-adjusted return`
      };
    } else if (irrToWaccMultiple >= 5) {
      // IRR is 5-10× WACC → Strong at minimum
      investmentTier = {
        label: 'Strong',
        color: isDark ? colors.primary : 'text.primary',
        description: `IRR exceeds WACC by ${Math.round(irrToWaccMultiple)}× — strong risk-adjusted return`
      };
    } else if (energyOnlyPayback <= 12) {
      // Payback-based: beats ALL alternatives by 2x+
      investmentTier = { label: 'Exceptional', color: isDark ? colors.success : 'text.primary', description: 'Outperforms all alternative ECMs' };
    } else if (energyOnlyPayback <= 24) {
      // Payback-based: beats VFD/LED significantly
      investmentTier = { label: 'Strong', color: isDark ? colors.primary : 'text.primary', description: 'Superior to VFD, LED, and replacement' };
    } else if (energyOnlyPayback <= 36) {
      // Payback-based: matches or beats VFD/LED
      investmentTier = { label: 'Acceptable', color: isDark ? colors.warning : 'text.primary', description: 'Comparable to leading ECMs' };
    } else {
      // Worse than alternatives
      investmentTier = { label: 'Marginal', color: isDark ? colors.error : 'text.primary', description: 'Review against alternatives' };
    }

    // ═══════════════════════════════════════════════════════════════
    // CAPITAL EQUIPMENT DEFERMENT - Life extension value as capex savings
    // ═══════════════════════════════════════════════════════════════
    const costPerTon = companyConfig?.equipmentCostPerTon;
    let capitalDeferment = null;
    if (costPerTon && totalTonnage > 0) {
      const replacementCycle = companyConfig?.equipmentReplacementCycle || 10;
      const lifeExtensionPercent = 0.30; // 30% life extension from PermaFrost

      // Total fleet replacement value based on tonnage
      const totalFleetValue = totalTonnage * costPerTon;

      // Annual replacement value (assuming uniform replacement across cycle)
      const annualReplacementValue = totalFleetValue / replacementCycle;

      // Annual replacement units
      const annualReplacementUnits = totalEquipment / replacementCycle;

      // Years of deferral = life extension as years
      const deferralYears = replacementCycle * lifeExtensionPercent;

      // Capital value deferred over the deferral window
      const capitalDeferred = annualReplacementValue * deferralYears;

      // Annual capex reduction
      const annualCapexReduction = annualReplacementValue * lifeExtensionPercent;

      capitalDeferment = {
        totalFleetValue,
        annualReplacementValue,
        annualReplacementUnits,
        deferralYears,
        capitalDeferred,
        annualCapexReduction,
        replacementCycle,
        lifeExtensionPercent,
        costPerTon,
        totalTonnage,
      };
    }

    return {
      capex,
      annualValue,
      energyOnlySavings,
      paybackMonths: marketingPayback,
      energyOnlyPayback,
      trueCashBreakevenMonths,
      breakEvenThresholdPct,
      valueMultiplier,
      npv6Component,
      npvEnergyOnly,
      fiveYearRoi,
      irrRange,
      irrRangeDisplay,
      fiveYearValue,
      roiPerDollar,
      capitalAtRiskMonths,
      strategicScore,
      discountRate: discountRate * 100,
      showMissionCritical,
      missionCriticalValue,
      valueBreakdown,
      investmentTier,
      // Key Modeling Inputs for analyst trust
      totalTonnage,
      totalEquipment,
      electricityRate,
      treatmentCostPerTon,
      easOperatingHours,
      totalAnnualValue: annualValue,
      // Capital Equipment Deferment (requires equipmentCostPerTon input)
      capitalDeferment,
    };
  }, [roiResults, companyConfig, paymentMode]);

  // ═══════════════════════════════════════════════════════════════
  // CORPUS COMPARISON - Match user equipment to 254 PE-certified studies
  // ═══════════════════════════════════════════════════════════════
  const corpusComparison = useMemo(() => {
    if (!corpusData || !roiResults?.individual) return null;

    const equipmentDist = corpusData.equipment_distribution?.primary_types;
    const performanceData = corpusData.performance_by_equipment_type;
    if (!equipmentDist) return null;

    // Get unique user equipment types
    const userEquipmentTypes = [...new Set(
      roiResults.individual.map(e => e.equipment_type?.toLowerCase())
    )].filter(Boolean);

    // Map user types to corpus types
    const typeMapping = {
      'chiller': 'Chiller',
      'rtu': 'RTU',
      'crac': 'CRAC',
      'split': 'DX_Split',
      'dx split': 'DX_Split',
      'dx_split': 'DX_Split',
    };

    const matches = [];
    userEquipmentTypes.forEach(type => {
      const corpusKey = typeMapping[type];
      const corpusEntry = equipmentDist[corpusKey];
      const perfEntry = performanceData?.[corpusKey];
      if (corpusEntry) {
        matches.push({
          type: corpusEntry.full_name || corpusKey,
          shortType: corpusKey,
          caseCount: corpusEntry.count,
          percentOfTotal: corpusEntry.percent_of_total,
          improvementRange: perfEntry?.typical_efficiency_improvement || '15-30%',
        });
      }
    });

    // Calculate confidence based on total matched studies
    const totalMatches = matches.reduce((sum, m) => sum + m.caseCount, 0);
    const confidence = totalMatches >= 50 ? 'Strong' : totalMatches >= 10 ? 'Good' : 'Limited';

    return {
      matches,
      totalMatches,
      confidence,
      corpusTotal: corpusData.data_source?.total_measurements || 254,
      meanImprovement: corpusData.data_source?.mean_efficiency_improvement || '23.2%',
    };
  }, [corpusData, roiResults]);

  if (!analysis) return null;

  // Format breakeven months professionally (CFO feedback: "0" looks like marketing)
  const formatBreakeven = (months) => {
    if (months < 1) return '<1';
    return months.toFixed(0);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Dynamic ranking for opportunity cost comparison
  // Score = IRR_low - (payback_months / 3) → penalizes longer paybacks fairly
  const calculateOpportunityCostRanking = () => {
    const permaFrostPayback = analysis.trueCashBreakevenMonths || 0;
    const permaFrostIrrLow = analysis.irrRange?.low || 0;

    // Calculate scores for all investments
    const investments = [
      {
        option: 'PermaFrost NMR Treatment',
        expectedReturn: analysis.irrRangeDisplay,
        risk: 'Low',
        payback: `${formatBreakeven(permaFrostPayback)} mo`,
        score: permaFrostIrrLow - (permaFrostPayback / 3),
        highlight: true
      },
      {
        option: 'VFD Installation',
        expectedReturn: '30-50%',
        risk: 'Low',
        payback: '24-36 mo',
        score: 30 - (30 / 3) // 30% IRR low, 30 mo avg payback
      },
      {
        option: 'LED Lighting Retrofit',
        expectedReturn: '25-35%',
        risk: 'Low',
        payback: '30-48 mo',
        score: 25 - (39 / 3) // 25% IRR low, 39 mo avg payback
      },
      {
        option: 'Core Business Expansion',
        expectedReturn: '15-25%',
        risk: 'Medium',
        payback: '36-60 mo',
        score: 15 - (48 / 3) // 15% IRR low, 48 mo avg payback
      },
      {
        option: 'Equipment Replacement',
        expectedReturn: '12-15%',
        risk: 'Low',
        payback: '60-84 mo',
        score: 12 - (72 / 3) // 12% IRR low, 72 mo avg payback
      },
      {
        option: 'Market Investments (S&P)',
        expectedReturn: '8-12%',
        risk: 'Medium',
        payback: 'Liquid',
        score: 8 - 0 // 8% IRR low, no payback penalty (liquid)
      },
      {
        option: 'Debt Reduction',
        expectedReturn: '5-8%',
        risk: 'Very Low',
        payback: 'Ongoing',
        score: 5 - 0 // 5% IRR low, no payback penalty (ongoing)
      },
    ];

    // Sort by score descending and assign ranks
    return investments
      .sort((a, b) => b.score - a.score)
      .map((inv, idx) => ({ ...inv, rank: idx + 1 }));
  };

  const opportunityCostData = calculateOpportunityCostRanking();

  return (
    <Box sx={{ my: 4 }}>
      {/* Section Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              letterSpacing: '-0.01em',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <AccountBalanceIcon sx={{ color: isDark ? colors.teal : '#1a1a1a' }} />
            CFO Capital Allocation Analysis
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Fleet deployment economics for {analysis?.totalEquipment || '—'} units | {analysis ? formatCurrency(analysis.annualValue) : '—'}/year
          </Typography>
        </Box>
        <IconButton onClick={() => setExpanded(!expanded)} size="small">
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Box
          sx={{
            bgcolor: 'background.paper',
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            overflow: 'hidden',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '3px',
              background: (theme) => theme.palette.mode === 'dark'
                ? 'linear-gradient(90deg, #4ECDC4, #A0CE4E, #64B5F6)'
                : 'linear-gradient(90deg, #0D7377, #5B7F24, #1868DB)',
              borderRadius: '8px 8px 0 0',
            },
          }}
        >
          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* PORTFOLIO HERO - Fleet-level metrics lead the story */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          <Box sx={{ p: 3, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
            <Grid container spacing={3}>
              {/* Total Equipment */}
              <Grid item xs={6} md={3}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    background: (theme) => theme.palette.mode === 'dark'
                      ? 'rgba(99, 179, 237, 0.08)'
                      : 'rgba(99, 179, 237, 0.12)',
                    border: (theme) => theme.palette.mode === 'dark'
                      ? '1px solid rgba(99, 179, 237, 0.2)'
                      : '1px solid rgba(99, 179, 237, 0.3)',
                    textAlign: 'center',
                    height: '100%',
                  }}
                >
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.6rem' }}>
                    Portfolio Size
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '1.75rem',
                      fontWeight: 800,
                      fontFamily: '"JetBrains Mono", monospace',
                      color: isDark ? colors.blue : 'text.primary',
                      lineHeight: 1.2,
                      mt: 0.5,
                    }}
                  >
                    {analysis.totalEquipment}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    units | {analysis.totalTonnage?.toFixed(0) || '—'} tons
                  </Typography>
                </Box>
              </Grid>

              {/* Total Annual Value */}
              <Grid item xs={6} md={3}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    background: (theme) => theme.palette.mode === 'dark'
                      ? 'rgba(160, 206, 78, 0.08)'
                      : 'rgba(160, 206, 78, 0.12)',
                    border: (theme) => theme.palette.mode === 'dark'
                      ? '1px solid rgba(160, 206, 78, 0.2)'
                      : '1px solid rgba(160, 206, 78, 0.3)',
                    textAlign: 'center',
                    height: '100%',
                  }}
                >
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.6rem' }}>
                    Annual Value
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '1.75rem',
                      fontWeight: 800,
                      fontFamily: '"JetBrains Mono", monospace',
                      color: isDark ? colors.primary : 'text.primary',
                      lineHeight: 1.2,
                      mt: 0.5,
                    }}
                  >
                    {formatCurrency(analysis.annualValue)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    portfolio-wide savings
                  </Typography>
                </Box>
              </Grid>

              {/* Portfolio Payback */}
              <Grid item xs={6} md={3}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    background: (theme) => theme.palette.mode === 'dark'
                      ? 'rgba(78, 205, 196, 0.08)'
                      : 'rgba(78, 205, 196, 0.12)',
                    border: (theme) => theme.palette.mode === 'dark'
                      ? '1px solid rgba(78, 205, 196, 0.2)'
                      : '1px solid rgba(78, 205, 196, 0.3)',
                    textAlign: 'center',
                    height: '100%',
                  }}
                >
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.6rem' }}>
                    Portfolio Payback
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '1.75rem',
                      fontWeight: 800,
                      fontFamily: '"JetBrains Mono", monospace',
                      color: isDark ? colors.teal : 'text.primary',
                      lineHeight: 1.2,
                      mt: 0.5,
                    }}
                  >
                    {formatBreakeven(analysis.energyOnlyPayback)} mo
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    energy-only (decision-grade)
                  </Typography>
                </Box>
              </Grid>

              {/* 5-Year NPV */}
              <Grid item xs={6} md={3}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    background: (theme) => theme.palette.mode === 'dark'
                      ? 'rgba(76, 175, 80, 0.08)'
                      : 'rgba(76, 175, 80, 0.12)',
                    border: (theme) => theme.palette.mode === 'dark'
                      ? '1px solid rgba(76, 175, 80, 0.2)'
                      : '1px solid rgba(76, 175, 80, 0.3)',
                    textAlign: 'center',
                    height: '100%',
                  }}
                >
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.6rem' }}>
                    5-Year NPV
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '1.75rem',
                      fontWeight: 800,
                      fontFamily: '"JetBrains Mono", monospace',
                      color: isDark ? colors.success : 'text.primary',
                      lineHeight: 1.2,
                      mt: 0.5,
                    }}
                  >
                    {formatCurrency(analysis.npvEnergyOnly)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    at {analysis.discountRate}% discount rate
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* ═══════════════════════════════════════════════════════════════ */}
            {/* CFO COVER NOTE - Copy/paste ready for executive emails */}
            {/* ═══════════════════════════════════════════════════════════════ */}
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 2,
                background: (theme) => theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.03)'
                  : 'rgba(0, 0, 0, 0.02)',
                border: (theme) => theme.palette.mode === 'dark'
                  ? '1px dashed rgba(255, 255, 255, 0.15)'
                  : '1px dashed rgba(0, 0, 0, 0.15)',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  fontSize: '0.6rem',
                  display: 'block',
                  mb: 1,
                }}
              >
                Executive Summary — Copy/Paste Ready
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', lineHeight: 1.7 }}>
                "This recommendation is based on an evidence-validated, physics-based efficiency process
                procured under contractual performance guarantees. It does not require site-specific pilot
                testing; verification occurs through aggregate utility cost reduction post-deployment.
                Downside risk is capped, capital exposure is minimized, and results scale linearly across
                the installed base."
              </Typography>
            </Box>

            {/* Portfolio Context Banner - Dynamic based on portfolio size */}
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 2,
                background: `linear-gradient(135deg, rgba(99, 179, 237, 0.08) 0%, rgba(160, 206, 78, 0.05) 100%)`,
                border: `1px solid ${analysis.investmentTier?.color || colors.primary}25`,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Chip
                label={analysis.investmentTier?.label || 'Strong'}
                sx={{
                  background: `${analysis.investmentTier?.color || colors.primary}20`,
                  color: analysis.investmentTier?.color || colors.primary,
                  fontWeight: 700,
                  fontSize: '0.7rem',
                }}
              />
              <Typography variant="body2" color="text.secondary">
                {analysis.totalEquipment <= 10
                  ? 'Proof of concept — validates unit economics for fleet expansion'
                  : analysis.totalEquipment <= 100
                  ? 'Mid-scale deployment — validates operational policy for broader rollout'
                  : 'Fleet-scale program — systematic efficiency lever across installed base'}
              </Typography>
            </Box>

            {/* Capital-at-Risk Callout */}
            {paymentMode === 'threeYear' && (
              <Box
                sx={{
                  mt: 2,
                  p: 1.5,
                  borderRadius: 1,
                  background: (theme) => theme.palette.mode === 'dark'
                    ? 'rgba(76, 175, 80, 0.08)'
                    : 'rgba(76, 175, 80, 0.12)',
                  border: (theme) => theme.palette.mode === 'dark'
                    ? '1px solid rgba(76, 175, 80, 0.2)'
                    : '1px solid rgba(76, 175, 80, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <CheckCircleIcon sx={{ color: isDark ? colors.success : 'text.primary', fontSize: '1.1rem' }} />
                <Typography variant="body2" sx={{ color: isDark ? colors.success : 'text.primary', fontWeight: 600 }}>
                  Zero capital-at-risk window — 3-year payment is cash-positive from Month 1
                </Typography>
              </Box>
            )}

            {/* Portfolio Approval Summary */}
            <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, background: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)', border: (theme) => `1px solid ${theme.palette.divider}` }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                <strong>Portfolio Approval:</strong> {formatBreakeven(analysis.energyOnlyPayback)}-month payback across {analysis.totalEquipment} units
                at {formatCurrency(analysis.capex)} total investment. Energy-only metric used for decision-grade approval.
              </Typography>
              {/* Cost of Inaction - Timing Urgency */}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Deferring this portfolio decision costs {formatCurrency(analysis.energyOnlySavings / 12)}/month in verified energy waste
                ({formatCurrency(analysis.energyOnlySavings / 12 / (analysis.totalEquipment || 1))}/month per unit average).
              </Typography>
              {/* Outcome Range - Expected variance */}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Expected portfolio outcome: {formatCurrency(analysis.energyOnlySavings)} – {formatCurrency(analysis.annualValue)}/year
                (energy-only to full 6-component value based on 116 PE-certified studies).
              </Typography>
            </Box>

            {/* ═══════════════════════════════════════════════════════════════ */}
            {/* DECISION BASIS - Kills "test first" reflex immediately */}
            {/* ═══════════════════════════════════════════════════════════════ */}
            <Box
              sx={{
                mt: 2,
                p: 2.5,
                borderRadius: 2,
                background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.08) 0%, rgba(78, 205, 196, 0.05) 100%)',
                border: '1px solid rgba(76, 175, 80, 0.25)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <VerifiedIcon sx={{ color: isDark ? colors.success : 'text.primary', fontSize: '1.25rem' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isDark ? colors.success : '#1a1a1a' }}>
                  Decision Basis
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                This investment decision is based on documented third-party field evidence and contractual
                performance guarantees and third party site-specific pilot testing. The technology has been validated
                across a large external corpus of Professional Engineer–certified measurements and is procured
                under enforceable performance terms.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Additional local testing is not required for approval, as performance verification occurs
                at the portfolio level through observable utility cost reductions.
              </Typography>
            </Box>
          </Box>

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* FINANCIAL METRICS - Energy-Only vs 6-Component Comparison */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          <Accordion
            expanded={expandedSections.financial}
            onChange={() => toggleSection('financial')}
            sx={{
              background: 'transparent',
              boxShadow: 'none',
              '&:before': { display: 'none' },
              '& .MuiAccordionSummary-root': {
                borderBottom: (theme) => expandedSections.financial ? `1px solid ${theme.palette.divider}` : 'none',
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary' }} />}
              sx={{ px: 3 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUpIcon sx={{ color: isDark ? colors.primary : 'text.primary', fontSize: '1.25rem' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Financial Metrics Comparison
                </Typography>
                <Chip
                  label="PE-Certified Data"
                  size="small"
                  sx={{
                    ml: 1,
                    height: 20,
                    fontSize: '0.6rem',
                    background: 'rgba(78, 205, 196, 0.15)',
                    color: isDark ? colors.teal : 'text.primary',
                  }}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 3, pb: 3 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: 'text.secondary', fontWeight: 600, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>Metric</TableCell>
                      <TableCell align="right" sx={{ color: isDark ? colors.orange : 'text.primary', fontWeight: 600, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>Energy-Only</TableCell>
                      <TableCell align="right" sx={{ color: isDark ? colors.primary : 'text.primary', fontWeight: 600, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>6-Component Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Annual Value */}
                    <TableRow sx={{ '&:nth-of-type(odd)': { background: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' } }}>
                      <TableCell sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>Annual Value</TableCell>
                      <TableCell align="right" sx={{ fontFamily: '"JetBrains Mono", monospace', borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
                        {formatCurrency(analysis.energyOnlySavings)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: '"JetBrains Mono", monospace', color: isDark ? colors.primary : 'text.primary', borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
                        {formatCurrency(analysis.annualValue)}
                      </TableCell>
                    </TableRow>

                    {/* True Cash Breakeven */}
                    <TableRow sx={{ '&:nth-of-type(odd)': { background: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' } }}>
                      <TableCell sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          True Cash Breakeven
                          <Chip label="Decision Grade" size="small" sx={{ height: 16, fontSize: '0.55rem', background: 'rgba(78, 205, 196, 0.15)', color: isDark ? colors.teal : '#1a1a1a' }} />
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: '"JetBrains Mono", monospace', borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
                        {formatBreakeven(analysis.energyOnlyPayback)} mo
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: '"JetBrains Mono", monospace', color: isDark ? colors.primary : 'text.primary', borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
                        {formatBreakeven(analysis.trueCashBreakevenMonths)} mo
                      </TableCell>
                    </TableRow>

                    {/* 5-Year NPV */}
                    <TableRow sx={{ '&:nth-of-type(odd)': { background: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' } }}>
                      <TableCell sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>5-Year NPV</TableCell>
                      <TableCell align="right" sx={{ fontFamily: '"JetBrains Mono", monospace', borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
                        {formatCurrency(analysis.npvEnergyOnly)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: '"JetBrains Mono", monospace', color: isDark ? colors.primary : 'text.primary', borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
                        {formatCurrency(analysis.npv6Component)}
                      </TableCell>
                    </TableRow>

                    {/* Year 1 ROI */}
                    <TableRow sx={{ '&:nth-of-type(odd)': { background: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' } }}>
                      <TableCell sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>Year 1 ROI</TableCell>
                      <TableCell align="right" sx={{ fontFamily: '"JetBrains Mono", monospace', borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
                        {((analysis.energyOnlySavings / analysis.capex) * 100).toFixed(0)}%
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: '"JetBrains Mono", monospace', color: isDark ? colors.primary : 'text.primary', borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
                        {((analysis.annualValue / analysis.capex) * 100).toFixed(0)}%
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Data Source Note */}
              <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, background: 'rgba(78, 205, 196, 0.05)', border: '1px solid rgba(78, 205, 196, 0.15)' }}>
                <Typography variant="caption" sx={{ color: isDark ? colors.teal : 'text.primary', fontWeight: 600, display: 'block', mb: 0.5 }}>
                  Based on 116 PE-Certified Field Studies
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  Energy-Only: Directly measured kWh reduction • 6-Component: Includes validated avoided costs (degradation, demand, runtime, life extension{analysis.showMissionCritical && ', mission-critical protection'})
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* CAPITAL OPPORTUNITY COST */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          <Accordion
            expanded={expandedSections.opportunity}
            onChange={() => toggleSection('opportunity')}
            sx={{
              background: 'transparent',
              boxShadow: 'none',
              '&:before': { display: 'none' },
              '& .MuiAccordionSummary-root': {
                borderBottom: (theme) => expandedSections.opportunity ? `1px solid ${theme.palette.divider}` : 'none',
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary' }} />}
              sx={{ px: 3 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AssessmentIcon sx={{ color: isDark ? colors.blue : 'text.primary', fontSize: '1.25rem' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Capital Opportunity Cost
                </Typography>
                <Chip
                  label="vs Alternative Investments"
                  size="small"
                  sx={{
                    ml: 1,
                    height: 20,
                    fontSize: '0.6rem',
                    background: 'rgba(99, 179, 237, 0.15)',
                    color: isDark ? colors.blue : 'text.primary',
                  }}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 3, pb: 3 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: 'text.secondary', fontWeight: 600, borderBottom: (theme) => `1px solid ${theme.palette.divider}`, width: '40%' }}>Investment Option</TableCell>
                      <TableCell align="center" sx={{ color: 'text.secondary', fontWeight: 600, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>Expected Return</TableCell>
                      <TableCell align="center" sx={{ color: 'text.secondary', fontWeight: 600, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>Risk</TableCell>
                      <TableCell align="center" sx={{ color: 'text.secondary', fontWeight: 600, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>Cash Breakeven</TableCell>
                      <TableCell align="center" sx={{ color: 'text.secondary', fontWeight: 600, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>Rank</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {opportunityCostData.map((row) => (
                      <TableRow
                        key={row.option}
                        sx={{
                          '&:nth-of-type(odd)': { background: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' },
                          ...(row.highlight && {
                            background: 'rgba(160, 206, 78, 0.08) !important',
                            '& td': { fontWeight: 600 },
                          }),
                        }}
                      >
                        <TableCell sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {row.highlight && <StarIcon sx={{ color: isDark ? colors.primary : 'text.primary', fontSize: '1rem' }} />}
                            {row.option}
                          </Box>
                        </TableCell>
                        <TableCell align="center" sx={{ fontFamily: '"JetBrains Mono", monospace', borderBottom: (theme) => `1px solid ${theme.palette.divider}`, color: row.highlight ? colors.primary : 'inherit' }}>
                          {row.expectedReturn}
                        </TableCell>
                        <TableCell align="center" sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
                          <Chip
                            label={row.risk}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.6rem',
                              background: (theme) => row.risk === 'Low'
                                ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(46, 125, 50, 0.15)')
                                : row.risk === 'Very Low'
                                ? (theme.palette.mode === 'dark' ? 'rgba(78, 205, 196, 0.15)' : 'rgba(13, 115, 119, 0.15)')
                                : (theme.palette.mode === 'dark' ? 'rgba(255, 152, 0, 0.15)' : 'rgba(180, 83, 9, 0.15)'),
                              color: row.risk === 'Low' ? colors.success :
                                     row.risk === 'Very Low' ? colors.teal :
                                     colors.warning,
                            }}
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ fontFamily: '"JetBrains Mono", monospace', borderBottom: (theme) => `1px solid ${theme.palette.divider}`, fontSize: '0.8rem' }}>
                          {row.payback}
                        </TableCell>
                        <TableCell align="center" sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: (theme) => row.rank === 1 ? colors.primary : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'),
                              color: row.rank === 1 ? '#1a1f25' : 'text.secondary',
                              fontWeight: 700,
                              fontSize: '0.75rem',
                              mx: 'auto',
                            }}
                          >
                            #{row.rank}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* RISK ASSESSMENT with Break-Even Threshold */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          <Accordion
            expanded={expandedSections.risk}
            onChange={() => toggleSection('risk')}
            sx={{
              background: 'transparent',
              boxShadow: 'none',
              '&:before': { display: 'none' },
              '& .MuiAccordionSummary-root': {
                borderBottom: (theme) => expandedSections.risk ? `1px solid ${theme.palette.divider}` : 'none',
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary' }} />}
              sx={{ px: 3 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <VerifiedIcon sx={{ color: isDark ? colors.success : 'text.primary', fontSize: '1.25rem' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Risk Assessment & Stress Test
                </Typography>
                <Chip
                  label="All Pass"
                  size="small"
                  icon={<CheckCircleIcon sx={{ fontSize: '0.9rem !important' }} />}
                  sx={{
                    ml: 1,
                    height: 20,
                    fontSize: '0.6rem',
                    background: 'rgba(76, 175, 80, 0.15)',
                    color: isDark ? colors.success : 'text.primary',
                    '& .MuiChip-icon': { color: isDark ? colors.success : '#1a1a1a' },
                  }}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 3, pb: 3 }}>
              {/* Break-Even Threshold Callout */}
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(78, 205, 196, 0.08) 100%)',
                  border: '1px solid rgba(76, 175, 80, 0.3)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CheckCircleIcon sx={{ color: isDark ? colors.success : '#1a1a1a' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isDark ? colors.success : '#1a1a1a' }}>
                    Downside Protection
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Investment remains NPV-positive even if only <strong style={{ color: isDark ? colors.success : '#1a1a1a' }}>{analysis.breakEvenThresholdPct}%</strong> of documented savings materialize.
                  Validated: Returns positive at {analysis.breakEvenThresholdPct}% of corpus-documented averages.
                </Typography>
              </Box>

              {/* Risk Register - Acknowledges failure modes explicitly */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isDark ? colors.teal : 'text.primary', mb: 2 }}>
                  Risk Register
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, borderBottom: (theme) => `1px solid ${theme.palette.divider}`, width: '30%' }}>Risk</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600, borderBottom: (theme) => `1px solid ${theme.palette.divider}`, width: '15%' }}>Likelihood</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600, borderBottom: (theme) => `1px solid ${theme.palette.divider}`, width: '15%' }}>Impact</TableCell>
                        <TableCell sx={{ fontWeight: 600, borderBottom: (theme) => `1px solid ${theme.palette.divider}`, width: '40%' }}>Mitigation</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[
                        { risk: 'Savings variance from projection', likelihood: 'Low', impact: 'Low', mitigation: 'ASHRAE documents 2-3%/year efficiency loss as thermodynamic certainty (28% by year 10). 113 PE-certified studies confirm 23% average degradation. Treatment recovers this documented loss—variance is in timing, not occurrence.' },
                        { risk: 'Equipment replacement during treatment period', likelihood: 'Low', impact: 'None', mitigation: 'Product guarantee provides treatment for replacement system at no additional cost.' },
                        { risk: 'M&V measurement variability', likelihood: 'Low', impact: 'Low', mitigation: 'Chemical process — recovers existing efficiency loss or prevents future degradation. Outcome is physics-based, not variable.' },
                      ].map((row, idx) => (
                        <TableRow key={idx} sx={{ '&:nth-of-type(odd)': { background: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' } }}>
                          <TableCell sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}`, fontSize: '0.8rem' }}>{row.risk}</TableCell>
                          <TableCell align="center" sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
                            <Chip
                              label={row.likelihood}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: '0.6rem',
                                background: (theme) => row.likelihood === 'Low'
                                  ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(46, 125, 50, 0.15)')
                                  : (theme.palette.mode === 'dark' ? 'rgba(255, 152, 0, 0.15)' : 'rgba(180, 83, 9, 0.15)'),
                                color: row.likelihood === 'Low' ? colors.success : colors.warning,
                              }}
                            />
                          </TableCell>
                          <TableCell align="center" sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
                            <Chip
                              label={row.impact}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: '0.6rem',
                                background: (theme) => row.impact === 'Low'
                                  ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(46, 125, 50, 0.15)')
                                  : row.impact === 'High'
                                  ? (theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.15)' : 'rgba(198, 40, 40, 0.15)')
                                  : (theme.palette.mode === 'dark' ? 'rgba(255, 152, 0, 0.15)' : 'rgba(180, 83, 9, 0.15)'),
                                color: row.impact === 'Low' ? colors.success : row.impact === 'High' ? colors.error : colors.warning,
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}`, fontSize: '0.75rem' }}>{row.mitigation}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Universal Applicability - Competitive Differentiator */}
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  borderRadius: 2,
                  background: 'rgba(160, 206, 78, 0.08)',
                  border: '1px solid rgba(160, 206, 78, 0.25)',
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isDark ? colors.primary : 'text.primary', mb: 1 }}>
                  Universal Applicability
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Unlike VFDs, lighting retrofits, or other ECMs that require complex screening criteria,
                  PermaFrost NMR treatment applies to <strong style={{ color: isDark ? colors.primary : '#1a1a1a' }}>all operational HVAC equipment</strong>:
                </Typography>
                <Grid container spacing={2}>
                  {[
                    {
                      scenario: 'New Equipment',
                      status: '✓ Treat',
                      rationale: 'Prevents degradation before it starts — optimal ROI timing',
                      color: isDark ? colors.primary : 'text.primary',
                      hasInfo: true,
                      infoKey: 'new_equipment_roi'
                    },
                    {
                      scenario: 'Aged Equipment',
                      status: '✓ Treat',
                      rationale: 'Recovers lost capacity — typical 20-25% efficiency restoration',
                      color: isDark ? colors.primary : '#1a1a1a'
                    },
                    {
                      scenario: 'Low Runtime',
                      status: '✓ Treat',
                      rationale: 'Still consumes power, still degrades — economics scale proportionally',
                      color: isDark ? colors.primary : '#1a1a1a'
                    },
                    {
                      scenario: 'Replacement <12 months',
                      status: '✓ Treat',
                      rationale: 'Guarantee: product provided for replacement system at no cost',
                      color: isDark ? colors.teal : '#1a1a1a'
                    },
                  ].map((item, idx) => (
                    <Grid item xs={12} sm={6} key={idx}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Typography sx={{ color: item.color, fontWeight: 700, fontSize: '0.9rem' }}>
                          {item.status}
                        </Typography>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.scenario}</Typography>
                            {item.hasInfo && (
                              <IconButton
                                size="small"
                                onClick={() => setExplanationModal(item.infoKey)}
                                sx={{
                                  p: 0.25,
                                  color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.35)' : 'rgba(0, 0, 0, 0.35)',
                                  '&:hover': { color: isDark ? colors.primary : 'text.primary', background: 'rgba(160, 206, 78, 0.1)' }
                                }}
                              >
                                <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                            {item.rationale}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                {/* Minimal Exclusion Criteria */}
                <Box sx={{ mt: 2, pt: 1.5, borderTop: (theme) => `1px solid ${theme.palette.divider}` }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    <strong>Only exclusion:</strong> Equipment scheduled for decommissioning within 6 months
                    (insufficient time for treatment to deliver value).
                  </Typography>
                </Box>
              </Box>

              {/* Corpus Validation - Show how user's equipment compares to PE-certified studies */}
              {corpusComparison && corpusComparison.matches.length > 0 && (
                <Box
                  sx={{
                    mb: 3,
                    p: 2,
                    borderRadius: 2,
                    background: 'rgba(160, 206, 78, 0.08)',
                    border: '1px solid rgba(160, 206, 78, 0.25)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isDark ? colors.primary : '#1a1a1a' }}>
                      Corpus Validation
                    </Typography>
                    <Chip
                      label={`${corpusComparison.confidence} Corpus Support`}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        background: corpusComparison.confidence === 'Strong' ? 'rgba(76, 175, 80, 0.2)' :
                                   corpusComparison.confidence === 'Good' ? 'rgba(160, 206, 78, 0.2)' :
                                   'rgba(255, 152, 0, 0.2)',
                        color: corpusComparison.confidence === 'Strong' ? colors.success :
                               corpusComparison.confidence === 'Good' ? colors.primary :
                               colors.warning
                      }}
                    />
                  </Box>

                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    Your equipment types are validated by <strong style={{ color: isDark ? colors.primary : '#1a1a1a' }}>{corpusComparison.totalMatches}</strong> PE-certified studies:
                  </Typography>

                  {/* Equipment Type Matches */}
                  <Grid container spacing={2}>
                    {corpusComparison.matches.map((match, idx) => (
                      <Grid item xs={6} sm={3} key={idx}>
                        <Box sx={{ textAlign: 'center', p: 1.5, background: 'rgba(0,0,0,0.2)', borderRadius: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem' }}>
                            {match.type}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: isDark ? colors.primary : 'text.primary', my: 0.5 }}>
                            {match.caseCount} studies
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                            {match.improvementRange} improvement
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>

                  {/* Overall Corpus Stats */}
                  <Box sx={{ mt: 2, pt: 1.5, borderTop: (theme) => `1px solid ${theme.palette.divider}` }}>
                    <Typography variant="caption" color="text.secondary">
                      <strong>Total Corpus:</strong> {corpusComparison.corpusTotal} PE-certified measurements | {corpusComparison.meanImprovement} mean improvement | IPMVP Option B compliant
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Rejection Criteria - Only genuine deal-breakers */}
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  borderRadius: 2,
                  background: 'rgba(244, 67, 54, 0.05)',
                  border: '1px solid rgba(244, 67, 54, 0.2)',
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isDark ? colors.error : 'text.primary', mb: 1 }}>
                  Rejection Criteria
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  <Typography component="li" variant="caption" color="text.secondary">
                    Equipment physically non-operational or beyond repair
                  </Typography>
                  <Typography component="li" variant="caption" color="text.secondary">
                    Treatment cost exceeds equipment replacement cost
                  </Typography>
                  {analysis.investmentTier.label === 'Marginal' && (
                    <Typography component="li" variant="caption" color="text.secondary">
                      IRR below company WACC (for Marginal tier only)
                    </Typography>
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, fontStyle: 'italic' }}>
                  Note: Near-term replacement is not a rejection criterion — product guarantee covers replacement systems at no additional cost.
                </Typography>
              </Box>

              <Grid container spacing={2}>
                {/* Hurdle Rate Checks */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 2, borderRadius: 2, background: 'rgba(76, 175, 80, 0.05)', border: '1px solid rgba(76, 175, 80, 0.2)' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isDark ? colors.success : 'text.primary', mb: 2 }}>
                      Hurdle Rate Verification
                    </Typography>
                    {[
                      { label: `Company WACC (${analysis.discountRate}%)`, status: 'PASS', detail: `IRR ${analysis.irrRangeDisplay} exceeds WACC by ${(analysis.irrRange.low / analysis.discountRate).toFixed(0)}×` },
                      { label: 'Required Payback (<36 mo)', status: 'PASS', detail: `${formatBreakeven(analysis.trueCashBreakevenMonths)} mo true cash breakeven` },
                      { label: `Break-Even Threshold (${analysis.breakEvenThresholdPct}%)`, status: 'PASS', detail: `${100 - analysis.breakEvenThresholdPct}% downside protection` },
                    ].map((check, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{check.label}</Typography>
                          <Typography variant="caption" color="text.secondary">{check.detail}</Typography>
                        </Box>
                        <Chip
                          label={check.status}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            background: 'rgba(76, 175, 80, 0.2)',
                            color: isDark ? colors.success : 'text.primary',
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                </Grid>

                {/* Risk Categories */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 2, borderRadius: 2, background: 'rgba(78, 205, 196, 0.05)', border: '1px solid rgba(78, 205, 196, 0.2)' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isDark ? colors.teal : 'text.primary', mb: 2 }}>
                      Risk Categories
                    </Typography>
                    {[
                      { category: 'Technology Risk', level: 'Very Low', reason: '116 PE-certified studies, 15 years installed history' },
                      { category: 'Execution Risk', level: 'Very Low', reason: 'No operational disruption during treatment' },
                      { category: 'Regulatory Risk', level: 'Very Low', reason: 'No refrigerants or disposal' },
                      { category: 'Vendor Risk', level: 'Very Low', reason: 'Product delivered; install by in-house, contracted, or Powertron Global service' },
                    ].map((risk, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{risk.category}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>{risk.reason}</Typography>
                        </Box>
                        <Chip
                          label={risk.level}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '0.6rem',
                            ml: 1,
                            background: risk.level === 'Very Low' ? 'rgba(78, 205, 196, 0.2)' : 'rgba(76, 175, 80, 0.2)',
                            color: isDark ? (risk.level === 'Very Low' ? colors.teal : colors.success) : '#1a1a1a',
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                </Grid>
              </Grid>

              {/* Key Modeling Inputs - Analyst Trust */}
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  borderRadius: 2,
                  background: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                  Key Modeling Inputs
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={4} md={2}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Energy Rate</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: '"JetBrains Mono", monospace' }}>
                      ${analysis.electricityRate?.toFixed(3) || '0.100'}/kWh
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Treatment Cost</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: '"JetBrains Mono", monospace' }}>
                      ${analysis.treatmentCostPerTon?.toFixed(0) || '125'}/ton
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Discount Rate</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: '"JetBrains Mono", monospace' }}>
                      {analysis.discountRate?.toFixed(0) || '8'}% WACC
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Operating Hours</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: '"JetBrains Mono", monospace' }}>
                      {(analysis.easOperatingHours || companyConfig?.operatingHours || 4500).toLocaleString()} hrs/yr
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Avg Load Factor</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: '"JetBrains Mono", monospace' }}>
                      {companyConfig?.avgLoadFactor ? `${(companyConfig.avgLoadFactor * 100).toFixed(0)}%` : '75%'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Equipment</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: '"JetBrains Mono", monospace' }}>
                      {analysis.totalEquipment || 0} units
                    </Typography>
                  </Grid>
                </Grid>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, fontStyle: 'italic' }}>
                  Source: Company Setup configuration and uploaded equipment data
                </Typography>
              </Box>

              {/* ═══════════════════════════════════════════════════════════════ */}
              {/* PROCUREMENT-SAFE LANGUAGE BLOCKS */}
              {/* ═══════════════════════════════════════════════════════════════ */}

              {/* Evidence Standard */}
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  borderRadius: 2,
                  background: 'rgba(78, 205, 196, 0.05)',
                  border: '1px solid rgba(78, 205, 196, 0.2)',
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isDark ? colors.teal : 'text.primary', mb: 1 }}>
                  Evidence Standard
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  The performance assumptions used in this analysis are derived from an external validation corpus
                  comprising 254 Professional Engineer–certified field measurements across multiple equipment classes
                  and operating environments, compliant with IPMVP Option B measurement standards.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  This evidence base exceeds the validation threshold typically required for comparable energy
                  conservation measures such as VFD installations, lighting retrofits, or heat-transfer enhancements,
                  which are commonly deployed without site-specific pilots.
                </Typography>
              </Box>

              {/* Rationale for No Local Pilot */}
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 2,
                  background: 'rgba(160, 206, 78, 0.05)',
                  border: '1px solid rgba(160, 206, 78, 0.2)',
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isDark ? colors.primary : 'text.primary', mb: 1 }}>
                  Rationale for No Local Pilot Requirement
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Local pilot testing is not required because:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 1 }}>
                  <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Performance is governed by physical heat-transfer and friction mechanisms rather than behavioral, algorithmic, or control-logic variables
                  </Typography>
                  <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Downside risk is contractually transferred to the vendor via performance guarantees and financial remedies
                  </Typography>
                  <Typography component="li" variant="body2" color="text.secondary">
                    At portfolio scale, aggregate utility consumption provides sufficient verification signal, rendering unit-level noise immaterial
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  Requiring local pilots under these conditions would increase friction without materially reducing risk.
                </Typography>
              </Box>

              {/* Contractual Risk Transfer */}
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 2,
                  background: 'rgba(76, 175, 80, 0.05)',
                  border: '1px solid rgba(76, 175, 80, 0.2)',
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isDark ? colors.success : 'text.primary', mb: 1 }}>
                  Risk Allocation
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Performance risk associated with this investment is contractually borne by the vendor. In the event
                  that guaranteed performance thresholds are not met, contractual remedies apply, including financial
                  adjustment, refund, or transfer of value to replacement equipment as specified in the agreement.
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: isDark ? colors.success : '#1a1a1a' }}>
                  As a result, capital exposure is capped and asymmetrically favorable to the buyer.
                </Typography>
              </Box>

              {/* Portfolio-Level Verification */}
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 2,
                  background: 'rgba(99, 179, 237, 0.05)',
                  border: '1px solid rgba(99, 179, 237, 0.2)',
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isDark ? colors.blue : 'text.primary', mb: 1 }}>
                  Verification Approach
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Performance verification for this program occurs at the portfolio level through observable changes
                  in aggregate energy consumption and cost, as reflected in utility invoices.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  This approach aligns with standard enterprise practice for validating efficiency improvements
                  where individual asset-level variance is outweighed by portfolio-level signal.
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* CFO RECOMMENDATION with M&V Approval Conditions */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          <Accordion
            expanded={expandedSections.recommendation}
            onChange={() => toggleSection('recommendation')}
            sx={{
              background: 'transparent',
              boxShadow: 'none',
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary' }} />}
              sx={{ px: 3 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon sx={{ color: isDark ? colors.primary : 'text.primary', fontSize: '1.25rem' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  CFO Recommendation
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 3, pb: 3 }}>
              {/* Recommendation Card */}
              <Box
                sx={{
                  p: 3,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, rgba(160, 206, 78, 0.12) 0%, rgba(78, 205, 196, 0.08) 100%)',
                  border: '2px solid rgba(160, 206, 78, 0.3)',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {/* Dynamic Approval Badge - Based on investment tier */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 2,
                    py: 0.75,
                    borderRadius: 1,
                    background: analysis.investmentTier.label === 'Exceptional' ? 'rgba(76, 175, 80, 0.2)' :
                               analysis.investmentTier.label === 'Strong' ? 'rgba(160, 206, 78, 0.2)' :
                               analysis.investmentTier.label === 'Acceptable' ? 'rgba(255, 152, 0, 0.2)' :
                               'rgba(244, 67, 54, 0.2)',
                    border: `1px solid ${analysis.investmentTier.color}40`,
                  }}
                >
                  <CheckCircleIcon sx={{ color: analysis.investmentTier.color, fontSize: '1.25rem' }} />
                  <Typography sx={{ fontWeight: 800, color: analysis.investmentTier.color, letterSpacing: '0.05em', fontSize: '0.85rem' }}>
                    {analysis.investmentTier.label === 'Marginal' ? 'REVIEW REQUIRED' : 'Approval Recommended — Decision Grade'}
                  </Typography>
                </Box>

                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, pr: 22 }}>
                  Investment Decision
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  This analysis validates unit economics for a repeatable operating policy, not a single-asset capital project.
                </Typography>

                <Grid container spacing={3}>
                  {/* Rationale Points */}
                  <Grid item xs={12} md={8}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem' }}>
                      Decision-Grade Rationale
                    </Typography>
                    {[
                      { text: `IRR ${analysis.irrRangeDisplay} exceeds ${analysis.discountRate}% WACC by ${(analysis.irrRange.low / analysis.discountRate).toFixed(0)}× (energy-only to full value)` },
                      { text: `True cash breakeven in ${formatBreakeven(analysis.trueCashBreakevenMonths)} months using verified energy savings only` },
                      { text: `Validated: Returns positive even at ${analysis.breakEvenThresholdPct}% of documented averages` },
                      { text: paymentMode === 'threeYear' ? 'Zero capital-at-risk with 3-year payment structure' : `${formatBreakeven(analysis.trueCashBreakevenMonths)}-month capital-at-risk window (upfront)` },
                      { text: 'Low execution risk: No operational disruption, reversible, proven technology' },
                    ].map((point, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                        <CheckCircleIcon sx={{ color: isDark ? colors.success : 'text.primary', fontSize: '0.9rem', mt: 0.25 }} />
                        <Typography variant="body2" color="text.secondary">{point.text}</Typography>
                      </Box>
                    ))}
                  </Grid>

                  {/* Strategic Score */}
                  <Grid item xs={12} md={4}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        background: 'rgba(0,0,0,0.2)',
                        textAlign: 'center',
                      }}
                    >
                      <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.65rem' }}>
                        Strategic Fit Score
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '2.5rem',
                          fontWeight: 800,
                          fontFamily: '"JetBrains Mono", monospace',
                          color: isDark ? colors.primary : 'text.primary',
                          lineHeight: 1,
                          mt: 1,
                        }}
                      >
                        {analysis.strategicScore.average}
                        <Typography component="span" sx={{ fontSize: '1rem', color: 'text.secondary' }}>/5</Typography>
                      </Typography>
                      <Box sx={{ mt: 1.5, textAlign: 'left' }}>
                        {[
                          { label: 'Margin Improvement', score: 5 },
                          { label: 'Operational Resilience', score: 5 },
                          { label: 'Scalability', score: 5 },
                          { label: 'Execution Simplicity', score: 5 },
                        ].map((criterion, idx) => (
                          <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>{criterion.label}</Typography>
                            <Typography variant="caption" sx={{ fontFamily: '"JetBrains Mono", monospace', color: isDark ? colors.primary : 'text.primary', fontSize: '0.7rem' }}>
                              {criterion.score}/5
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Grid>
                </Grid>

                {/* M&V Protocol Specification - Hard specs, not vague promises */}
                <Box
                  sx={{
                    mt: 3,
                    p: 2,
                    borderRadius: 2,
                    background: 'rgba(78, 205, 196, 0.08)',
                    border: '1px solid rgba(78, 205, 196, 0.2)',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isDark ? colors.teal : 'text.primary', mb: 1.5 }}>
                    M&V Protocol Specification
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3} md={3}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Metric</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>kW/ton</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>at matched load</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3} md={3}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Baseline</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Not required</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>116 PE-certified studies</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3} md={3}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Verification</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>1-2 years</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>post-treatment</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3} md={3}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Pass Threshold</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: isDark ? colors.primary : '#1a1a1a' }}>Near design spec</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>recovery varies by age/runtime</Typography>
                    </Grid>
                  </Grid>
                </Box>

                {/* Approval Conditions */}
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    borderRadius: 2,
                    background: 'rgba(255, 152, 0, 0.08)',
                    border: '1px solid rgba(255, 152, 0, 0.25)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <WarningAmberIcon sx={{ color: isDark ? colors.warning : 'text.primary', fontSize: '1.1rem' }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isDark ? colors.warning : '#1a1a1a' }}>
                      Approval Conditions
                    </Typography>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Performance Guarantee</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Restore to design efficiency — contractually guaranteed
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Payment Structure</Typography>
                      <Typography variant="caption" color="text.secondary">
                        3-year deferred recommended. Upfront only if tax incentives improve after-tax IRR.
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Equipment Lifecycle</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Covered by product guarantee — older systems gain life extension value
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                {/* Post-Approval Governance - Signals seriousness */}
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    borderRadius: 2,
                    background: 'rgba(99, 179, 237, 0.08)',
                    border: '1px solid rgba(99, 179, 237, 0.25)',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isDark ? colors.blue : 'text.primary', mb: 1.5 }}>
                    Post-Approval Governance
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Owner</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Facilities / Energy Manager</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Review Cadence</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>90-day post-install validation</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>If Underperforming</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Handled contractually</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Accountability</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Facilities: execution • Finance: verification • CFO: policy approval
                      </Typography>
                    </Grid>
                  </Grid>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, fontStyle: 'italic' }}>
                    Facilities is not accountable for savings realization; verification is financial and contractual.
                  </Typography>
                </Box>

                {/* Fleet-Context Final Judgment - Installed-base framing */}
                <Box
                  sx={{
                    mt: 3,
                    p: 2,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, rgba(160, 206, 78, 0.08) 0%, rgba(78, 205, 196, 0.05) 100%)',
                    border: '1px solid rgba(160, 206, 78, 0.25)',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isDark ? colors.primary : 'text.primary', mb: 1 }}>
                    Installed-Base Context
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {analysis.investmentTier.label === 'Exceptional' ? (
                      <>
                        Unit-level economics are <strong style={{ color: isDark ? colors.primary : '#1a1a1a' }}>exceptional</strong> on an energy-only basis,
                        with rapid payback and strong IRR. When applied across a large installed base,
                        it represents a <strong style={{ color: isDark ? colors.primary : '#1a1a1a' }}>high-value efficiency program</strong> with
                        compounding returns at scale.
                      </>
                    ) : analysis.investmentTier.label === 'Strong' ? (
                      <>
                        Unit-level economics are <strong style={{ color: isDark ? colors.primary : '#1a1a1a' }}>strong</strong> on an energy-only basis,
                        clearing investment hurdles comfortably. When applied across a large installed base,
                        it represents a <strong style={{ color: isDark ? colors.primary : '#1a1a1a' }}>scalable efficiency lever</strong> with
                        attractive returns at portfolio scale.
                      </>
                    ) : (
                      <>
                        While unit-level economics are modest on an energy-only basis, this investment clears WACC with
                        limited downside and rapid verification. When applied selectively across a large installed base,
                        it represents a <strong style={{ color: isDark ? colors.primary : '#1a1a1a' }}>scalable loss-reduction and efficiency lever</strong> rather
                        than a stand-alone capital project.
                      </>
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                    {analysis.investmentTier.label === 'Exceptional' || analysis.investmentTier.label === 'Strong' ? (
                      <>Approval is appropriate as a standard operating policy for all qualifying assets.</>
                    ) : (
                      <>Approval is appropriate as a repeatable operating policy under deferred payment, subject to standard screening and verification criteria.</>
                    )}
                  </Typography>
                </Box>

                {/* Payment Structure Policy - CFO-approved framing */}
                <Box
                  sx={{
                    mt: 3,
                    p: 2,
                    borderRadius: 2,
                    background: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                    Payment Structure Policy
                  </Typography>

                  {/* Preferred Structure */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Box sx={{
                        px: 1,
                        py: 0.25,
                        borderRadius: 0.5,
                        background: 'rgba(78, 205, 196, 0.2)',
                        border: '1px solid rgba(78, 205, 196, 0.4)',
                      }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: isDark ? colors.teal : 'text.primary', fontSize: '0.7rem' }}>
                          PREFERRED (DEFAULT)
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>3-Year Deferred Payment</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      The economically preferred structure. Eliminates capital-at-risk, ensures cash-flow positive
                      operation from month one, and aligns payment with realized savings.
                    </Typography>
                  </Box>

                  {/* Permitted Alternative */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Box sx={{
                        px: 1,
                        py: 0.25,
                        borderRadius: 0.5,
                        background: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                        border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.15)' : '1px solid rgba(0, 0, 0, 0.15)',
                      }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.7rem' }}>
                          PERMITTED (BY PREFERENCE)
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Upfront Payment</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      May be selected where organizational policy, procurement preference, or accounting treatment
                      favors immediate settlement. Returns capital faster but concentrates risk.
                    </Typography>
                  </Box>
                </Box>

                {/* ═══════════════════════════════════════════════════════════════ */}
                {/* PROCUREMENT-SAFE FINAL STATEMENTS */}
                {/* ═══════════════════════════════════════════════════════════════ */}

                {/* Not Experimental - Critical single sentence */}
                <Box
                  sx={{
                    mt: 3,
                    p: 2,
                    borderRadius: 2,
                    background: 'rgba(76, 175, 80, 0.08)',
                    border: '2px solid rgba(76, 175, 80, 0.3)',
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 700, color: isDark ? colors.success : '#1a1a1a' }}>
                    This deployment does not constitute an experimental, developmental, or speculative technology trial.
                  </Typography>
                </Box>

                {/* Standard Operating Rule */}
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    borderRadius: 2,
                    background: 'rgba(160, 206, 78, 0.08)',
                    border: '1px solid rgba(160, 206, 78, 0.25)',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isDark ? colors.primary : 'text.primary', mb: 1 }}>
                    Standard Operating Rule
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    For equipment meeting the documented qualification criteria, this treatment is recommended as a
                    default operating efficiency action, subject to contractual terms and standard governance oversight.
                  </Typography>
                </Box>

                {/* Capital Treatment */}
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    borderRadius: 2,
                    background: 'rgba(99, 179, 237, 0.05)',
                    border: '1px solid rgba(99, 179, 237, 0.2)',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isDark ? colors.blue : 'text.primary', mb: 1 }}>
                    Capital Treatment
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Given the deferred payment option and immediate cash-flow alignment with realized savings,
                    this investment functions economically as an operating efficiency measure rather than a
                    traditional capital project, notwithstanding accounting classification preferences.
                  </Typography>
                </Box>

                {/* Governance Disclaimer */}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'center', opacity: 0.7 }}>
                  This tool output is intended to support capital allocation decisions and does not substitute for standard governance review.
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* PORTFOLIO SCALING - Fleet-Level Value Creation */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          <Accordion
            expanded={expandedSections.portfolio}
            onChange={() => toggleSection('portfolio')}
            sx={{
              background: 'transparent',
              boxShadow: 'none',
              '&:before': { display: 'none' },
              '& .MuiAccordionSummary-root': {
                borderBottom: (theme) => expandedSections.portfolio ? `1px solid ${theme.palette.divider}` : 'none',
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary' }} />}
              sx={{ px: 3 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUpIcon sx={{ color: isDark ? colors.primary : 'text.primary', fontSize: '1.25rem' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Unit Economics & Scale Projections
                </Typography>
                <Chip
                  label="Per-Unit Validation"
                  size="small"
                  sx={{
                    ml: 1,
                    height: 20,
                    fontSize: '0.6rem',
                    background: 'rgba(160, 206, 78, 0.15)',
                    color: isDark ? colors.primary : 'text.primary',
                  }}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 3, pb: 3 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Per-unit economics validated for repeatable deployment across your installed base.
                Scale projections show value at fleet expansion levels.
              </Typography>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>Fleet Scale</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>Annual Savings</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>Treatment Cost</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>5-Year Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(() => {
                      const unitSavings = analysis.energyOnlySavings / (analysis.totalEquipment || 1);
                      const unitCost = analysis.capex / (analysis.totalEquipment || 1);
                      const scaleRows = [
                        { scale: 'Current', units: analysis.totalEquipment || 1, isCurrent: true },
                        { scale: '100 units', units: 100, isCurrent: false },
                        { scale: '1,000 units', units: 1000, isCurrent: false },
                        { scale: '10,000 units', units: 10000, isCurrent: false },
                        { scale: '100,000 units', units: 100000, isCurrent: false },
                      ];
                      return scaleRows.map((row) => (
                        <TableRow
                          key={row.scale}
                          sx={{
                            ...(row.isCurrent && { background: 'rgba(160, 206, 78, 0.08)' }),
                            '&:nth-of-type(odd):not(:first-of-type)': { background: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' },
                          }}
                        >
                          <TableCell sx={{ fontWeight: row.isCurrent ? 700 : 400, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
                            {row.scale}
                            {row.isCurrent && (
                              <Chip
                                label="This analysis"
                                size="small"
                                sx={{
                                  ml: 1,
                                  height: 16,
                                  fontSize: '0.55rem',
                                  background: 'rgba(160, 206, 78, 0.2)',
                                  color: isDark ? colors.primary : 'text.primary',
                                }}
                              />
                            )}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontFamily: '"JetBrains Mono", monospace',
                              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                              fontSize: '0.85rem',
                            }}
                          >
                            {formatCurrency(unitSavings * row.units)}/yr
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontFamily: '"JetBrains Mono", monospace',
                              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                              fontSize: '0.85rem',
                            }}
                          >
                            {formatCurrency(unitCost * row.units)}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontFamily: '"JetBrains Mono", monospace',
                              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                              color: isDark ? colors.primary : 'text.primary',
                              fontWeight: 600,
                              fontSize: '0.85rem',
                            }}
                          >
                            {formatCurrency(unitSavings * row.units * 5)}
                          </TableCell>
                        </TableRow>
                      ));
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box
                sx={{
                  mt: 2,
                  p: 1.5,
                  borderRadius: 1,
                  background: (theme) => theme.palette.mode === 'dark' ? 'rgba(160, 206, 78, 0.08)' : 'rgba(0, 0, 0, 0.03)',
                  border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(160, 206, 78, 0.2)' : '1px solid rgba(0, 0, 0, 0.1)',
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  Portfolio Insight
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  At fleet scale, this is not a marginal investment—it's a systematic efficiency lever.
                  The current analysis validates unit economics for a repeatable operating policy.
                </Typography>
              </Box>

              {/* Deployment Roadmap - Controlled Expansion */}
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  borderRadius: 2,
                  background: (theme) => theme.palette.mode === 'dark' ? 'rgba(99, 179, 237, 0.08)' : 'rgba(0, 0, 0, 0.03)',
                  border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(99, 179, 237, 0.25)' : '1px solid rgba(0, 0, 0, 0.1)',
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', mb: 1.5 }}>
                  Deployment Roadmap
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  Start small, scale fast as results prove out:
                </Typography>
                <Grid container spacing={2}>
                  {[
                    {
                      phase: 'Phase 1: Proof of Concept',
                      scope: '5% or greater of portfolio',
                      gate: 'Verified savings confirm projections',
                      color: isDark ? colors.teal : 'text.primary',
                    },
                    {
                      phase: 'Phase 2: Rapid Scale',
                      scope: 'Remaining portfolio',
                      gate: 'Continue rollout as results prove out',
                      color: isDark ? colors.primary : 'text.primary',
                    },
                  ].map((phase, idx) => (
                    <Grid item xs={12} md={6} key={idx}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 1,
                          background: (theme) => theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : '#fff',
                          borderLeft: (theme) => theme.palette.mode === 'dark' ? `3px solid ${phase.color}` : '3px solid rgba(0, 0, 0, 0.2)',
                          border: (theme) => theme.palette.mode === 'dark' ? undefined : '1px solid rgba(0, 0, 0, 0.1)',
                          height: '100%',
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                          {phase.phase}
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', color: 'text.primary' }}>
                          {phase.scope}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', fontStyle: 'italic' }}>
                          Gate: {phase.gate}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              {/* Capital Equipment Deferment Analysis */}
              {analysis.capitalDeferment && (
                <Box
                  sx={{
                    mt: 3,
                    p: 2,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, rgba(78, 205, 196, 0.08) 0%, rgba(160, 206, 78, 0.05) 100%)',
                    border: '1px solid rgba(78, 205, 196, 0.25)',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isDark ? colors.teal : 'text.primary', mb: 1.5 }}>
                    Capital Equipment Deferment
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    PermaFrost extends equipment life by ~{(analysis.capitalDeferment.lifeExtensionPercent * 100).toFixed(0)}%,
                    deferring replacement purchases and freeing capital.
                  </Typography>

                  <Grid container spacing={2}>
                    {/* Total Fleet Value */}
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ textAlign: 'center', p: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Fleet Replacement Value
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: '"JetBrains Mono", monospace' }}>
                          {formatCurrency(analysis.capitalDeferment.totalFleetValue)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatNumber(analysis.capitalDeferment.totalTonnage, 0)} tons @ ${formatNumber(analysis.capitalDeferment.costPerTon, 0)}/ton
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Deferral Window */}
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ textAlign: 'center', p: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Life Extension
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: '"JetBrains Mono", monospace', color: isDark ? colors.teal : '#1a1a1a' }}>
                          +{analysis.capitalDeferment.deferralYears.toFixed(1)} yrs
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ({(analysis.capitalDeferment.lifeExtensionPercent * 100).toFixed(0)}% of {analysis.capitalDeferment.replacementCycle}-yr cycle)
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Capital Deferred */}
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ textAlign: 'center', p: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Capital Deferred
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: '"JetBrains Mono", monospace', color: isDark ? colors.primary : '#1a1a1a' }}>
                          {formatCurrency(analysis.capitalDeferment.capitalDeferred)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          over {analysis.capitalDeferment.deferralYears.toFixed(1)} years
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Annual Capex Reduction */}
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ textAlign: 'center', p: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Annual Capex Savings
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: '"JetBrains Mono", monospace', color: isDark ? colors.primary : '#1a1a1a' }}>
                          {formatCurrency(analysis.capitalDeferment.annualCapexReduction)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">/year freed</Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Balance Sheet Benefits */}
                  <Box sx={{ mt: 2, pt: 1.5, borderTop: (theme) => `1px solid ${theme.palette.divider}` }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      <strong>Balance Sheet Impact:</strong> Deferred equipment purchases improve working capital,
                      extend depreciation schedules, and reduce annual capex budget requirements.
                      For older equipment, added lubricity can extend life beyond the {analysis.capitalDeferment.deferralYears.toFixed(1)}-year estimate.
                    </Typography>
                  </Box>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        </Box>
      </Collapse>

      {/* Explanation Modal for New Equipment ROI */}
      <MetricExplanationModal
        metric={explanationModal}
        open={!!explanationModal}
        onClose={() => setExplanationModal(null)}
      />
    </Box>
  );
}
